import qrcode from "qrcode-generator";

/**
 * A scannable QR code, as inline SVG.
 *
 * Nothing in the product rendered one, which meant the whole QR ordering flow
 * had no entry point: tokens were generated and regenerated correctly and
 * there was no way to get a code onto a table. The back office said so rather
 * than drawing a placeholder that looked like a code, which was the right call
 * and not a fix.
 *
 * **This is the repository's one new runtime dependency** and it was a last
 * resort (`13-house-style.md`). QR is ISO/IEC 18004 — Reed-Solomon over
 * GF(256), eight mask patterns scored against four penalty rules — and a
 * hand-rolled encoder would produce images whose only real test is a phone
 * nobody has in CI. `qrcode-generator` is Kazuhiko Arase's reference
 * implementation: MIT, no transitive dependencies, and ships its own types.
 *
 * SVG rather than canvas so it stays framework-agnostic and printable: this
 * package must not reach for the DOM beyond what React gives it, and a table
 * tent is printed at whatever size the paper is.
 */

/**
 * `M` recovers ~15% and is the level a printed table tent wants: a code that
 * lives on a table gets wet, scratched and covered by a plate. `L` is smaller
 * and gives up the margin that makes it still scan on a worn card; `Q` and `H`
 * cost modules that a phone camera then has to resolve at the same physical
 * size.
 */
const ERROR_CORRECTION = "M";

export type QrCodeProps = {
  /** What a phone should end up at. Encoded verbatim. */
  readonly value: string;
  /** Rendered edge length in CSS pixels. The SVG scales; the matrix does not. */
  readonly size?: number;
  /**
   * The quiet zone, in modules. Four is the standard's minimum and dropping it
   * is the most common reason a printed code will not scan — the scanner needs
   * the blank border to find the symbol at all.
   */
  readonly margin?: number;
  readonly className?: string;
  /** Announced to a screen reader; the code itself is decorative to one. */
  readonly title?: string;
};

export function QrCode({ value, size = 192, margin = 4, className, title }: QrCodeProps) {
  const { count, path } = qrPath(value, margin);

  return (
    <svg
      // The viewBox is the module grid, so the symbol is resolution-independent
      // and a printer gets crisp edges at any paper size.
      viewBox={`0 0 ${count} ${count}`}
      width={size}
      height={size}
      className={className}
      role="img"
      aria-label={title ?? value}
      // `crispEdges` matters: anti-aliased module boundaries are what turns a
      // small printed code into one that reads intermittently.
      shapeRendering="crispEdges"
    >
      {/* Painted, not left transparent. A dark page behind a QR code inverts
          it, and an inverted code is one many scanners refuse. */}
      <rect width={count} height={count} fill="#ffffff" />
      <path d={path} fill="#000000" />
    </svg>
  );
}

/**
 * The dark modules as one SVG path, plus the grid size including the quiet
 * zone.
 *
 * One path rather than a rect per module: a version-6 code is 41×41, so a
 * naive render is up to 1,681 elements and the browser lays out every one of
 * them. Runs of adjacent dark modules in a row collapse into a single
 * horizontal segment, which is both smaller and what keeps the printed edges
 * from showing hairlines between neighbouring rects.
 */
export function qrPath(value: string, margin = 4): { count: number; path: string } {
  // Type 0 lets the library pick the smallest version that fits, so a short
  // URL is a coarse code a cheap camera can read across a table.
  const qr = qrcode(0, ERROR_CORRECTION);
  qr.addData(value);
  qr.make();

  const modules = qr.getModuleCount();
  const count = modules + margin * 2;
  const segments: string[] = [];

  for (let row = 0; row < modules; row += 1) {
    let runStart: number | null = null;
    for (let column = 0; column <= modules; column += 1) {
      const dark = column < modules && qr.isDark(row, column);
      if (dark && runStart === null) runStart = column;
      if (!dark && runStart !== null) {
        segments.push(
          `M${runStart + margin} ${row + margin}h${column - runStart}v1h-${column - runStart}z`,
        );
        runStart = null;
      }
    }
  }

  return { count, path: segments.join("") };
}
